/**
 * NewDiagramConfig — "describe the install, get a diagram" wizard.
 *
 * Two big upgrades over the first cut:
 *   - Catalog pickers are SEARCH-DRIVEN, not select-dropdowns. With
 *     25+ inverters and growing it was painful to scroll a select.
 *     Search by manufacturer / model / power band hits exactly the
 *     right model in 3 keystrokes.
 *   - Per-MPPT layout. Real installs rarely have a uniform "X strings
 *     × Y panels" across every MPPT — it's normal for MPPT 1 to have
 *     2× 10-panel strings and MPPT 2 to have 1× 12-panel string. Each
 *     MPPT now has its own row of inputs.
 *
 * Wizard collects:
 *   - Project address + drawing number
 *   - Inverter model (searchable) + count + per-MPPT layout
 *   - Panel model (searchable)
 *   - Optional battery (searchable) + count
 *   - Sub-board / connection point
 *
 * On Generate it builds a Topology and calls loadTopology(). The
 * existing autoLayout pipeline does the rest.
 *
 * First-version constraints (call out in the wizard if relevant):
 *   - All inverters share the same model and same per-MPPT layout.
 *     For mixed-inverter jobs, generate the dominant case and edit
 *     outliers afterwards.
 *   - All panels are one type. Mixing arrays (existing + new) is
 *     done after generation via the per-inverter panel editor.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  INVERTER_CATALOG,
  inverterFromCatalog,
  type InverterCatalogEntry,
} from '../catalog/inverters';
import {
  PANEL_CATALOG,
  type PanelCatalogEntry,
} from '../catalog/panels';
import {
  BATTERY_CATALOG,
  batteryFromCatalog,
  type BatteryCatalogEntry,
} from '../catalog/batteries';
import { useEditorStore } from '../store/editor';
import type {
  Topology,
  PanelType,
  Inverter,
  Battery,
  Switchgear,
  MPPT,
} from '../types/topology';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// One row of per-MPPT config. We store these flat in the wizard so
// "strings count" and "panels per string" can be edited independently
// for each MPPT before we build the Topology.
interface MpptConfig {
  strings: number;
  panelsPerString: number;
}

export function NewDiagramConfig({ open, onOpenChange }: Props) {
  const loadTopology = useEditorStore((s) => s.loadTopology);
  const askConfirm   = useEditorStore((s) => s.askConfirm);
  const currentTopology = useEditorStore((s) => s.topology);
  const hasUserContent = useEditorStore((s) =>
    s.nodes.some(
      (n) =>
        n.id !== '__page_frame__' &&
        n.id !== '__title_block__' &&
        !n.id.startsWith('existing-boundary-')
    )
  );

  const [address, setAddress] = useState(
    currentTopology.project.address ?? ''
  );
  const [drawingNumber, setDrawingNumber] = useState(
    currentTopology.project.drawingNumber ?? 'SLD-001'
  );

  // Inverter selection — null until picked. Default to a sensible
  // residential 3-phase hybrid (SH10RT) which is the most common job.
  const [inverter, setInverter] = useState<InverterCatalogEntry | null>(
    () => INVERTER_CATALOG.find((e) => e.model === 'SH10RT') ?? INVERTER_CATALOG[0] ?? null
  );
  const [inverterQty, setInverterQty] = useState(1);

  // Per-MPPT configs sized to the chosen inverter. Initialised to
  // the catalog's default strings-per-MPPT × 10 panels.
  const [mppts, setMppts] = useState<MpptConfig[]>(() =>
    inverter
      ? Array.from({ length: inverter.mpptCount }, () => ({
          strings: inverter.defaultStringsPerMppt,
          panelsPerString: 10,
        }))
      : []
  );

  // When the inverter changes, resize the MPPT array. Preserve any
  // matching positions (so swapping a 2-MPPT model for another
  // 2-MPPT keeps your tweaks). Pad / trim as needed.
  const prevInverterModel = useRef(inverter?.model);
  useEffect(() => {
    if (!inverter) return;
    if (prevInverterModel.current === inverter.model) return;
    prevInverterModel.current = inverter.model;
    setMppts((prev) => {
      const next: MpptConfig[] = [];
      for (let i = 0; i < inverter.mpptCount; i++) {
        next.push(
          prev[i] ?? {
            strings: inverter.defaultStringsPerMppt,
            panelsPerString: 10,
          }
        );
      }
      return next;
    });
  }, [inverter]);

  const [panel, setPanel] = useState<PanelCatalogEntry | null>(
    () => PANEL_CATALOG[0] ?? null
  );

  const [battery, setBattery] = useState<BatteryCatalogEntry | null>(null);
  const [batteryQty, setBatteryQty] = useState<number>(1);

  const [subBoardPresent, setSubBoardPresent] = useState(false);
  const [solarVia, setSolarVia] = useState<'main' | 'sub'>('main');

  // Aggregate panel count + power summary, derived live from per-MPPT.
  const totalPanelsPerInverter = useMemo(
    () => mppts.reduce((n, m) => n + m.strings * m.panelsPerString, 0),
    [mppts]
  );
  const totalPanels = totalPanelsPerInverter * inverterQty;
  const totalKwDc = (totalPanels * (panel?.pmax ?? 0)) / 1000;
  const totalKwAc = (inverterQty * (inverter?.capacity ?? 0)) / 1000;

  // Per-MPPT validation. The wizard surfaces violations as soft
  // warnings beside each MPPT row so users see exactly which MPPT
  // is the problem, not just a generic "something's wrong".
  const mpptWarnings: Record<number, string[]> = {};
  if (inverter && panel) {
    mppts.forEach((m, idx) => {
      const list: string[] = [];
      const stringVoc = panel.voc * m.panelsPerString;
      const stringCurrent = panel.isc * m.strings;
      if (stringVoc > inverter.voltageRange[1]) {
        list.push(
          `String Voc ${stringVoc.toFixed(0)}V > inverter max ${inverter.voltageRange[1]}V — reduce panels`
        );
      } else if (stringVoc < inverter.voltageRange[0] * 0.7) {
        list.push(
          `String Voc ${stringVoc.toFixed(0)}V too low for MPPT (starts ${inverter.voltageRange[0]}V) — add panels`
        );
      }
      if (stringCurrent > inverter.maxCurrentPerMppt) {
        list.push(
          `${stringCurrent.toFixed(1)}A exceeds MPPT limit ${inverter.maxCurrentPerMppt}A — reduce strings`
        );
      }
      if (list.length) mpptWarnings[idx] = list;
    });
  }
  const couplingWarning =
    battery?.coupling === 'dc' && inverter?.type !== 'hybrid'
      ? 'DC-coupled battery selected but the chosen inverter isn\'t a hybrid. Pick a hybrid (e.g. SH10RT, SH25T) or switch to an AC-coupled battery.'
      : null;

  const blockingError: string | null =
    !inverter
      ? 'Pick an inverter model.'
      : !panel
      ? 'Pick a panel model.'
      : inverterQty < 1
      ? 'At least one inverter is required.'
      : mppts.some((m) => m.strings < 1 || m.panelsPerString < 1)
      ? 'Each MPPT needs at least 1 string × 1 panel.'
      : null;

  const close = () => onOpenChange(false);

  const buildAndLoad = async () => {
    if (blockingError || !inverter || !panel) return;
    if (hasUserContent) {
      const ok = await askConfirm({
        title: 'Replace current diagram?',
        message:
          'Generating a new diagram will discard the current one (Cmd/Ctrl+Z afterwards will not bring it back). Continue?',
        confirmLabel: 'Generate new',
        tone: 'warning',
      });
      if (!ok) return;
    }
    const t = buildTopology({
      address,
      drawingNumber,
      inverter,
      inverterQty,
      mppts,
      panel,
      battery,
      batteryQty,
      subBoardPresent,
      solarVia,
      installer: currentTopology.project.installer,
      revision: currentTopology.project.revision ?? 'A',
    });
    loadTopology(t);
    close();
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={close}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ndc-title"
        style={{
          width: 'min(640px, 94vw)',
          maxWidth: 'none',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div
          id="ndc-title"
          style={{
            fontSize: 'var(--fs-xl)',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            marginBottom: 'var(--space-1)',
          }}
        >
          New diagram
        </div>
        <div
          style={{
            fontSize: 'var(--fs-base)',
            color: 'var(--text-muted)',
            marginBottom: 'var(--space-4)',
          }}
        >
          Tell us about the install — quantities, models, per-MPPT string layout — and we'll draft the diagram. Edit any symbol afterwards.
        </div>

        <Section label="Project">
          <Field label="Site address">
            <input
              className="input"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Example St, Suburb, STATE 0000"
            />
          </Field>
          <Field label="Drawing number" inline>
            <input
              className="input"
              type="text"
              value={drawingNumber}
              onChange={(e) => setDrawingNumber(e.target.value)}
              style={{ maxWidth: 160 }}
              placeholder="SLD-001"
            />
          </Field>
        </Section>

        <Section label="Inverter">
          <CatalogSearchPicker<InverterCatalogEntry>
            value={inverter}
            entries={INVERTER_CATALOG}
            onPick={setInverter}
            displayPrimary={(e) => `${e.manufacturer} ${e.model}`}
            displaySecondary={(e) =>
              `${(e.capacity / 1000).toFixed(1)}kW · ${e.phases}ph · ${e.type}` +
              ` · ${e.mpptCount} MPPT${e.mpptCount > 1 ? 's' : ''}` +
              ` · ${e.maxCurrentPerMppt}A/MPPT` +
              (e.note ? ` · ${e.note}` : '')
            }
            searchHaystack={(e) =>
              `${e.manufacturer} ${e.model} ${e.type} ${e.phases}ph ${e.note ?? ''} ${(e.capacity / 1000).toFixed(0)}kw`
            }
            placeholder="Search inverters by manufacturer, model, kW, type…"
          />

          <Row>
            <Field label="Quantity" inline>
              <NumberInput value={inverterQty} onChange={setInverterQty} min={1} max={20} />
            </Field>
          </Row>

          {inverter && (
            <>
              <div
                style={{
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--text-muted)',
                  marginTop: 'var(--space-3)',
                  marginBottom: 'var(--space-2)',
                }}
              >
                Per-MPPT layout
                {inverterQty > 1 && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontWeight: 500,
                      textTransform: 'none',
                      letterSpacing: 0,
                      color: 'var(--text-faint)',
                    }}
                  >
                    (applied to all {inverterQty} inverters)
                  </span>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {mppts.map((m, idx) => (
                  <MpptRow
                    key={idx}
                    index={idx}
                    config={m}
                    warnings={mpptWarnings[idx] ?? []}
                    onChange={(next) =>
                      setMppts((prev) => prev.map((x, i) => (i === idx ? next : x)))
                    }
                    panelInfo={
                      panel
                        ? {
                            voc: panel.voc * m.panelsPerString,
                            current: panel.isc * m.strings,
                          }
                        : null
                    }
                  />
                ))}
              </div>
            </>
          )}
        </Section>

        <Section label="PV Panel">
          <CatalogSearchPicker<PanelCatalogEntry>
            value={panel}
            entries={PANEL_CATALOG}
            onPick={setPanel}
            displayPrimary={(p) => `${p.manufacturer} ${p.model}`}
            displaySecondary={(p) =>
              `${p.pmax}W · Voc ${p.voc}V · Isc ${p.isc}A` +
              (p.note ? ` · ${p.note}` : '')
            }
            searchHaystack={(p) =>
              `${p.manufacturer} ${p.model} ${p.note ?? ''} ${p.pmax}w`
            }
            placeholder="Search panels by manufacturer, model, wattage…"
          />
        </Section>

        <Section label="Battery (optional)">
          <CatalogSearchPicker<BatteryCatalogEntry>
            value={battery}
            entries={BATTERY_CATALOG}
            onPick={setBattery}
            displayPrimary={(b) => `${b.manufacturer} ${b.model}`}
            displaySecondary={(b) =>
              `${b.capacity}kWh · ${b.coupling.toUpperCase()}-coupled` +
              (b.note ? ` · ${b.note}` : '')
            }
            searchHaystack={(b) =>
              `${b.manufacturer} ${b.model} ${b.coupling} ${b.note ?? ''}`
            }
            placeholder="Search batteries — or leave empty for solar-only"
            allowClear
          />
          {battery && (
            <Field label="Quantity" inline>
              <NumberInput value={batteryQty} onChange={setBatteryQty} min={1} max={10} />
            </Field>
          )}
          {couplingWarning && (
            <div
              style={{
                marginTop: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--warning-50)',
                border: '1px solid var(--warning-500)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--fs-base)',
                color: 'var(--warning-700)',
              }}
            >
              {couplingWarning}
            </div>
          )}
        </Section>

        <Section label="Switchgear">
          <Toggle
            checked={subBoardPresent}
            onChange={setSubBoardPresent}
            label="Property has a sub-distribution board"
          />
          {subBoardPresent && (
            <div style={{ paddingLeft: 24, marginTop: 6 }}>
              <Toggle
                checked={solarVia === 'sub'}
                onChange={(v) => setSolarVia(v ? 'sub' : 'main')}
                label="Solar connects via the sub-board (not the main switch)"
              />
            </div>
          )}
        </Section>

        <div
          style={{
            background: 'var(--surface-3)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
            marginTop: 'var(--space-4)',
            fontSize: 'var(--fs-base)',
            color: 'var(--text-muted)',
          }}
        >
          <strong style={{ color: 'var(--text)' }}>Summary:</strong>{' '}
          {inverterQty}× {inverter?.model || '(no inverter)'},{' '}
          {totalPanels}× {panel?.model || '(no panel)'},{' '}
          <strong style={{ color: 'var(--text)' }}>{totalKwDc.toFixed(1)} kW DC</strong> /{' '}
          <strong style={{ color: 'var(--text)' }}>{totalKwAc.toFixed(1)} kW AC</strong>
          {battery && (
            <>
              {', '}
              {batteryQty}× {battery.model} ({(battery.capacity * batteryQty).toFixed(1)} kWh{batteryQty > 1 ? ' total' : ''})
            </>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-5)',
          }}
        >
          <button type="button" className="btn-secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => { void buildAndLoad(); }}
            disabled={!!blockingError}
            title={blockingError ?? 'Generate the diagram'}
          >
            Generate diagram
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CatalogSearchPicker — generic searchable picker used for inverter,
// panel and battery. Shows the current pick as a chip; clicking it
// opens an inline search panel that filters in real time.
// ─────────────────────────────────────────────────────────────────

interface CatalogPickerProps<T> {
  value: T | null;
  entries: readonly T[];
  onPick: (entry: T | null) => void;
  /** Header line of each row — manufacturer + model. */
  displayPrimary: (e: T) => string;
  /** Subline — specs, notes. */
  displaySecondary: (e: T) => string;
  /** Searchable text per entry — combined into a haystack. */
  searchHaystack: (e: T) => string;
  placeholder: string;
  /** Show a "(none)" / clear option. Used by Battery (optional). */
  allowClear?: boolean;
}

function CatalogSearchPicker<T>({
  value,
  entries,
  onPick,
  displayPrimary,
  displaySecondary,
  searchHaystack,
  placeholder,
  allowClear,
}: CatalogPickerProps<T>) {
  const [open, setOpen] = useState(!value); // expanded if nothing picked yet
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQuery('');
    }
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries.slice(0, 50);
    const tokens = q.split(/\s+/);
    return entries.filter((e) => {
      const hay = searchHaystack(e).toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [query, entries, searchHaystack]);

  // Selected card
  if (value && !open) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 'var(--space-3)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--surface)',
          marginBottom: 'var(--space-3)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--fs-md)',
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: 2,
            }}
          >
            {displayPrimary(value)}
          </div>
          <div
            style={{
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displaySecondary(value)}
          </div>
        </div>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => setOpen(true)}
        >
          Change
        </button>
        {allowClear && (
          <button
            type="button"
            className="btn-tertiary btn-sm"
            onClick={() => onPick(null)}
            title="Clear selection"
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  // Search panel
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface)',
        marginBottom: 'var(--space-3)',
        overflow: 'hidden',
      }}
    >
      <input
        ref={inputRef}
        type="search"
        className="input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        style={{
          border: 0,
          borderRadius: 0,
          borderBottom: '1px solid var(--border)',
        }}
      />
      <div
        style={{
          maxHeight: 220,
          overflowY: 'auto',
        }}
      >
        {allowClear && !query && (
          <PickerRow
            primary="None — solar only"
            secondary="Don't include a battery"
            onClick={() => {
              onPick(null);
              setOpen(false);
            }}
            isCurrent={value === null}
          />
        )}
        {matches.length === 0 ? (
          <div
            style={{
              padding: 'var(--space-3)',
              fontSize: 'var(--fs-base)',
              color: 'var(--text-faint)',
              textAlign: 'center',
            }}
          >
            No matches for <strong>{query}</strong>
          </div>
        ) : (
          matches.map((e, i) => (
            <PickerRow
              key={i}
              primary={displayPrimary(e)}
              secondary={displaySecondary(e)}
              isCurrent={value === e}
              onClick={() => {
                onPick(e);
                setOpen(false);
              }}
            />
          ))
        )}
      </div>
      {value && (
        <div
          style={{
            padding: 6,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            background: 'var(--surface-3)',
          }}
        >
          <button
            type="button"
            className="btn-tertiary btn-sm"
            onClick={() => setOpen(false)}
          >
            Keep current
          </button>
        </div>
      )}
    </div>
  );
}

function PickerRow({
  primary,
  secondary,
  onClick,
  isCurrent,
}: {
  primary: string;
  secondary: string;
  onClick: () => void;
  isCurrent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        background: isCurrent ? 'var(--accent-soft)' : 'transparent',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        color: 'var(--text)',
      }}
      onMouseEnter={(e) => {
        if (!isCurrent) e.currentTarget.style.background = 'var(--surface-3)';
      }}
      onMouseLeave={(e) => {
        if (!isCurrent) e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{primary}</div>
      <div
        style={{
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-muted)',
          marginTop: 1,
        }}
      >
        {secondary}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Per-MPPT row.
// ─────────────────────────────────────────────────────────────────

function MpptRow({
  index,
  config,
  onChange,
  warnings,
  panelInfo,
}: {
  index: number;
  config: MpptConfig;
  onChange: (next: MpptConfig) => void;
  warnings: string[];
  panelInfo: { voc: number; current: number } | null;
}) {
  const hasIssue = warnings.length > 0;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: 'var(--space-2) var(--space-3)',
        border: `1px solid ${hasIssue ? 'var(--warning-500)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)',
        background: hasIssue ? 'var(--warning-50)' : 'var(--surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div
          style={{
            fontSize: 'var(--fs-sm)',
            fontWeight: 700,
            color: 'var(--text)',
            minWidth: 64,
          }}
        >
          MPPT {index + 1}
        </div>
        <CompactNumberField
          label="Strings"
          value={config.strings}
          onChange={(v) => onChange({ ...config, strings: v })}
          min={1}
          max={6}
        />
        <CompactNumberField
          label="Panels each"
          value={config.panelsPerString}
          onChange={(v) => onChange({ ...config, panelsPerString: v })}
          min={1}
          max={40}
        />
        <div
          style={{
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-faint)',
            marginLeft: 'auto',
          }}
        >
          {config.strings * config.panelsPerString} panels
          {panelInfo && (
            <>
              {' · '}
              {panelInfo.voc.toFixed(0)}V Voc · {panelInfo.current.toFixed(1)}A
            </>
          )}
        </div>
      </div>
      {warnings.map((w, i) => (
        <div
          key={i}
          style={{
            fontSize: 'var(--fs-xs)',
            color: 'var(--warning-700)',
          }}
        >
          ⚠ {w}
        </div>
      ))}
    </div>
  );
}

function CompactNumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
        {label}
      </span>
      <input
        className="input"
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        style={{ width: 70, padding: '4px 8px' }}
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────
// Layout helpers — Section / Field / Row / NumberInput / Toggle
// ─────────────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 'var(--space-4)' }}>
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-faint)',
          marginBottom: 'var(--space-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        {label}
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  inline,
  children,
}: {
  label: string;
  inline?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        marginBottom: 'var(--space-3)',
        display: inline ? 'flex' : 'block',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
          marginBottom: inline ? 0 : 4,
          minWidth: inline ? 110 : undefined,
        }}
      >
        {label}
      </div>
      <div style={{ flex: inline ? 1 : undefined }}>{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
      {children}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <input
      className="input"
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(n);
      }}
      style={{ width: 80 }}
    />
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 'var(--fs-base)',
        color: 'var(--text)',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────
// Topology builder — translates wizard inputs into a Topology JSON.
// Now per-MPPT aware.
// ─────────────────────────────────────────────────────────────────

interface BuildArgs {
  address: string;
  drawingNumber: string;
  inverter: InverterCatalogEntry;
  inverterQty: number;
  mppts: MpptConfig[];
  panel: PanelCatalogEntry;
  battery: BatteryCatalogEntry | null;
  batteryQty: number;
  subBoardPresent: boolean;
  solarVia: 'main' | 'sub';
  installer: Topology['project']['installer'];
  revision: string;
}

function buildTopology(args: BuildArgs): Topology {
  // Build the Inverter list. We don't go through inverterFromCatalog
  // because that helper applies one global stringsPerMppt — we now
  // have per-MPPT detail. Hand-roll the MPPT array using args.mppts.
  const inverters: Inverter[] = Array.from({ length: args.inverterQty }, (_, i) => {
    const mppts: MPPT[] = args.mppts.map((m, j) => ({
      mpptNumber: j + 1,
      maxCurrent: args.inverter.maxCurrentPerMppt,
      voltageRange: args.inverter.voltageRange,
      strings: Array.from({ length: m.strings }, () => ({
        panels: m.panelsPerString,
      })),
    }));
    return {
      id: `inv${i + 1}`,
      manufacturer: args.inverter.manufacturer,
      model: args.inverter.model,
      capacity: args.inverter.capacity,
      phases: args.inverter.phases,
      type: args.inverter.type,
      mppts,
      panelTypeId: 'pt1',
    };
  });
  // Reference inverterFromCatalog so the import isn't tree-shaken
  // away when we may need it (e.g. future "use catalog defaults"
  // path). Cheap no-op call to keep the type imported.
  void inverterFromCatalog;

  const panelType: PanelType = {
    id: 'pt1',
    manufacturer: args.panel.manufacturer,
    model: args.panel.model,
    pmax: args.panel.pmax,
    voc: args.panel.voc,
    vmp: args.panel.vmp,
    isc: args.panel.isc,
    imp: args.panel.imp,
    fuseRating: args.panel.fuseRating,
  };

  const batteries: Battery[] = [];
  if (args.battery && args.batteryQty > 0) {
    const hybrids = inverters.filter((i) => i.type === 'hybrid');
    for (let i = 0; i < args.batteryQty; i++) {
      const id = `bat${i + 1}`;
      if (args.battery.coupling === 'dc') {
        const host = hybrids[Math.min(i, hybrids.length - 1)];
        batteries.push(
          batteryFromCatalog(args.battery, {
            id,
            connectedTo: host?.id,
          })
        );
      } else {
        batteries.push(batteryFromCatalog(args.battery, { id }));
      }
    }
  }

  const switchgear: Switchgear = {
    rooftopDcIsolator: true,
    acIsolator: true,
    energyMeter: true,
    serviceFuse: true,
    ctMetering: (args.inverter.capacity * args.inverterQty) > 15000,
    subBoard: args.subBoardPresent
      ? { present: true, label: 'SUB-BOARD' }
      : { present: false },
    solarConnectionPoint: args.subBoardPresent ? args.solarVia : 'main',
  };

  return {
    project: {
      address: args.address,
      installDate: new Date().toISOString().slice(0, 10),
      drawingNumber: args.drawingNumber,
      revision: args.revision,
      installer: args.installer,
    },
    panels: panelType,
    panelTypes: [panelType],
    inverters,
    batteries,
    switchgear,
    grid: {
      phases: args.inverter.phases,
      transformer: false,
    },
  };
}
